// shareStudio.js
//
// ESTÚDIO DAS ARTES — editor dentro do próprio app para arrumar, elemento por
// elemento, a imagem que é compartilhada.
//
// ── Por que editar o nó vivo, e não um "editor de imagem" ────────────────────
// O PNG não é desenhado à parte: o shareCanvas.js CLONA o mesmo `.shareCard`
// que está na tela e fotografa o clone (html2canvas). Qualquer estilo aplicado
// no elemento real entra no arquivo exatamente igual — arrastar o título 3px
// para cima na tela é arrastar 12px no PNG de 1080×1920 (a captura é 4×). Daí o
// estúdio mexer em `element.style` e não num canvas paralelo: o que se edita É
// a imagem gerada, não uma prévia parecida com ela.
//
// ── O que o estúdio NÃO pode fazer ───────────────────────────────────────────
// Vazar para o arquivo. A moldura do elemento selecionado é um ATRIBUTO
// (`data-studio-sel`) estilizado por CSS; o shareCanvas apaga esse atributo do
// clone antes de fotografar (limparEstudio). Sem isso a moldura sairia
// carimbada no story de quem estivesse com o estúdio aberto.
//
// ── Onde os ajustes valem: no ARQUIVO, não na amostra ────────────────────────
// A amostra do carrossel e a do prompt pós-registro ficam SEMPRE com o desenho
// original. Os ajustes entram só no clone que vira PNG (aplicarNaCaptura) e na
// tela apenas enquanto o estúdio está aberto, para dar para editar vendo.
// Por quê: a amostra é renderizada a 270×480 com as fontes e o antialias do
// aparelho; o PNG sai 4× pelo html2canvas, que arredonda e mede diferente. Um
// acerto de 4px que deixa o ARQUIVO perfeito costuma tortar a AMOSTRA. Eram dois
// destinos disputando um número só — agora cada um tem o seu.
//
// ── Camadas de liga/desliga ──────────────────────────────────────────────────
//   AJUSTES_LIGADOS (shareOverrides.js)  interruptor mestre, no código.
//   estado.aplicar                       liga/desliga os ajustes na hora, para
//                                        comparar antes/depois sem perder nada.
//   estado.modo                          mostra ou esconde o EDITOR, e é o que
//                                        deixa a amostra espelhar o arquivo.
//                                        Fica trancado até um toque longo
//                                        destravar — usuário comum nunca vê.
//
// ── Onde mora o que ──────────────────────────────────────────────────────────
// Um único registro no localStorage por aparelho. É preferência de autoria, não
// dado de usuário: não vale escrita no Firestore, e storage indisponível (aba
// privada, WebView restrita) NUNCA pode derrubar a tela de compartilhar — daí
// try/catch em toda leitura e escrita.
import { useEffect, useState } from 'react';

import { storySafeAreaVars } from '../../../../config/storyCanvas';
import { AJUSTES_LIGADOS, AJUSTES_PADRAO } from './shareOverrides';

const CHAVE = 'lesvita.share.studio.v2';

// Caminho reservado do próprio cartão (o nó que vira o PNG).
export const RAIZ = 'card';

// Atributos que o estúdio pendura no DOM. Guardam QUAIS propriedades ele
// escreveu, para conseguir desfazer sem apagar estilo inline de terceiros — o
// `background-image` da foto, por exemplo, é do ShareCard e não é nosso.
const ATTR_PROPS = 'data-studio-css';
const ATTR_VARS = 'data-studio-vars';
export const ATTR_SEL = 'data-studio-sel';

const PATCH_VAZIO = Object.freeze({
  dx: 0, dy: 0, escala: 1, giro: 0, fonte: 0, largura: 0, altura: 0,
  textoX: 0, textoY: 0, alinhar: '', peso: 0, entrelinha: 0, entreletras: 0,
  cor: '', opacidade: 1, esconder: false, vars: null,
});

const num = (valor, padrao = 0) => {
  const n = Number(valor);
  return Number.isFinite(n) ? n : padrao;
};

const clonar = (obj) => JSON.parse(JSON.stringify(obj || {}));

// ── patch ────────────────────────────────────────────────────────────────────
export const patchCompleto = (patch) => ({ ...PATCH_VAZIO, ...(patch || {}) });

// Patch que não muda nada é lixo: some do estado e do JSON exportado.
export function patchVazio(patch) {
  if (!patch) return true;
  const p = patchCompleto(patch);
  if (p.esconder) return false;
  if (p.vars && Object.keys(p.vars).length > 0) return false;
  return num(p.dx) === 0 && num(p.dy) === 0 && num(p.escala, 1) === 1
    && num(p.giro) === 0 && num(p.fonte) === 0 && num(p.largura) === 0
    && num(p.altura) === 0 && num(p.textoX) === 0 && num(p.textoY) === 0
    && !p.alinhar && num(p.peso) === 0 && num(p.entrelinha) === 0
    && num(p.entreletras) === 0 && !p.cor && num(p.opacidade, 1) === 1;
}

// ── caminho do elemento ──────────────────────────────────────────────────────
// Trilha `nome:índice` da raiz até o elemento. O NOME (data-el, quando o
// ShareCard batiza o bloco) deixa o JSON legível; o ÍNDICE separa irmãos
// iguais. Guardar os dois permite reencontrar o elemento mesmo quando a arte
// renderiza em quantidades diferentes — um treino com 2 métricas e outro com 4.
const nomeDe = (el) => el.getAttribute?.('data-el') || String(el.tagName || '').toLowerCase();

const segmento = (el) => {
  const irmaos = el.parentElement ? Array.from(el.parentElement.children) : [el];
  return `${nomeDe(el)}:${irmaos.indexOf(el)}`;
};

export function caminhoDoElemento(raiz, el) {
  if (!raiz || !el) return '';
  if (el === raiz) return RAIZ;
  const partes = [];
  let atual = el;
  while (atual && atual !== raiz) {
    partes.unshift(segmento(atual));
    atual = atual.parentElement;
  }
  return atual === raiz ? partes.join('/') : '';
}

export function resolverElemento(raiz, caminho) {
  if (!raiz) return null;
  if (!caminho || caminho === RAIZ) return raiz;
  let atual = raiz;
  const partes = caminho.split('/');
  for (let i = 0; i < partes.length; i += 1) {
    if (!atual) return null;
    const [nome, idxTexto] = partes[i].split(':');
    const filhos = Array.from(atual.children || []);
    const exato = filhos[num(idxTexto, -1)];
    if (exato && nomeDe(exato) === nome) {
      atual = exato;
    } else {
      // Render condicional mexeu nos índices: cai para o primeiro irmão de mesmo
      // nome. Acertar o vizinho é melhor que perder o ajuste inteiro.
      const porNome = filhos.filter((f) => nomeDe(f) === nome);
      atual = porNome[0] || null;
    }
  }
  return atual;
}

// Rótulo curto para a lista do painel. Elemento batizado mostra o nome; folha
// de texto mostra o próprio texto; o resto mostra a tag.
export function rotuloDoElemento(el) {
  if (!el) return '—';
  const nome = el.getAttribute?.('data-el');
  if (nome) return nome.replace(/-/g, ' ');
  const texto = (el.textContent || '').trim().replace(/\s+/g, ' ');
  if (texto && texto.length <= 22) return `"${texto}"`;
  return String(el.tagName || '?').toLowerCase();
}

// Elementos editáveis de um cartão, em ordem de leitura. Fica de fora o que não
// faz sentido mover: o interior do <svg> (o anel é editado inteiro) e os véus
// de degradê, que sangram até a borda de propósito.
export function listarElementos(raiz) {
  if (!raiz) return [];
  return Array.from(raiz.querySelectorAll('*')).filter((el) => {
    if (el.closest('svg')) return false;
    if (el.getAttribute('data-el') === 'fundo') return false;
    return true;
  });
}

// ── aplicar no DOM ───────────────────────────────────────────────────────────
function limparElemento(el) {
  const props = el.getAttribute?.(ATTR_PROPS);
  if (props) {
    props.split('|').forEach((p) => el.style.removeProperty(p));
    el.removeAttribute(ATTR_PROPS);
  }
  const vars = el.getAttribute?.(ATTR_VARS);
  if (vars) {
    vars.split('|').forEach((p) => el.style.removeProperty(p));
    el.removeAttribute(ATTR_VARS);
  }
}

function aplicarPatch(el, patchBruto) {
  const patch = patchCompleto(patchBruto);
  const props = [];
  const vars = [];

  if (patch.vars) {
    Object.keys(patch.vars).forEach((nome) => {
      const valor = patch.vars[nome];
      if (valor === '' || valor === null || valor === undefined) return;
      el.style.setProperty(nome, String(valor));
      vars.push(nome);
    });
  }

  if (patch.esconder) {
    el.style.setProperty('display', 'none', 'important');
    props.push('display');
  } else {
    const dx = num(patch.dx);
    const dy = num(patch.dy);
    const escala = num(patch.escala, 1);
    const giro = num(patch.giro);
    const partes = [];
    if (dx || dy) partes.push(`translate(${dx}px, ${dy}px)`);
    if (escala !== 1) partes.push(`scale(${escala})`);
    if (giro) partes.push(`rotate(${giro}deg)`);
    if (partes.length) {
      // `transform` acabou de ser limpo, então o computado é a base que vem do
      // CSS (o .sideRail nasce com translateY(-50%); a marca-d'água, girada).
      // O ajuste entra ANTES da base: assim arrastar 10px anda 10px na tela
      // mesmo num elemento girado — depois da base, andaria na diagonal.
      const base = window.getComputedStyle(el).transform;
      const composto = base && base !== 'none' ? `${partes.join(' ')} ${base}` : partes.join(' ');
      el.style.setProperty('transform', composto);
      props.push('transform');
    }
    if (num(patch.fonte) > 0) {
      el.style.setProperty('font-size', `${num(patch.fonte)}px`);
      props.push('font-size');
    }
    if (patch.cor) {
      // `important` porque .accentText já usa !important na cor.
      el.style.setProperty('color', String(patch.cor), 'important');
      props.push('color');
    }
    if (num(patch.peso) > 0) {
      el.style.setProperty('font-weight', String(num(patch.peso)));
      props.push('font-weight');
    }
    if (num(patch.entrelinha) > 0) {
      // Sem unidade: multiplicador do tamanho da fonte, como o CSS do cartão usa.
      el.style.setProperty('line-height', String(num(patch.entrelinha)));
      props.push('line-height');
    }
    if (num(patch.entreletras) !== 0) {
      el.style.setProperty('letter-spacing', `${num(patch.entreletras)}px`);
      props.push('letter-spacing');
    }
    if (patch.alinhar) {
      // `text-align` só manda em conteúdo de LINHA. Metade dos blocos do cartão
      // é flex (a coluna do .stat, a linha do .bottomMeta) e ali quem alinha é
      // o justify/align — sem isso o botão de alinhar não faria nada justo nos
      // blocos onde ele mais é preciso.
      el.style.setProperty('text-align', patch.alinhar);
      props.push('text-align');
      const css = window.getComputedStyle(el);
      if (css.display === 'flex' || css.display === 'inline-flex') {
        const ponta = patch.alinhar === 'right' ? 'flex-end'
          : (patch.alinhar === 'center' ? 'center' : 'flex-start');
        if (String(css.flexDirection).startsWith('column')) {
          el.style.setProperty('align-items', ponta);
          props.push('align-items');
        } else {
          el.style.setProperty('justify-content', ponta);
          props.push('justify-content');
        }
      }
    }
    const opacidade = num(patch.opacidade, 1);
    if (opacidade !== 1) {
      el.style.setProperty('opacity', String(opacidade));
      props.push('opacity');
    }
    if (num(patch.largura) > 0) {
      el.style.setProperty('width', `${num(patch.largura)}px`);
      el.style.setProperty('max-width', 'none');
      props.push('width', 'max-width');
    }
    if (num(patch.altura) > 0) {
      // `min-height` zerado junto: sem isso a pílula de macro (min-height: 59px)
      // ignoraria qualquer altura menor que a do CSS.
      el.style.setProperty('height', `${num(patch.altura)}px`);
      el.style.setProperty('min-height', '0');
      props.push('height', 'min-height');
    }

    // ── mover SÓ o texto, sem levar a caixa ──────────────────────────────────
    // O texto solto dentro de uma caixa (o "P 86g" da pílula, por exemplo) não é
    // um elemento: não dá para selecionar nem arrastar. O jeito de descê-lo sem
    // mexer na pílula é trocar respiro de cima por respiro de baixo — a soma dos
    // dois não muda, então a caixa fica do mesmo tamanho e só o conteúdo anda.
    // Caixa sem respiro nenhum não tem o que trocar: aí ela cresce mesmo.
    const textoX = num(patch.textoX);
    const textoY = num(patch.textoY);
    if (textoX || textoY) {
      // O respiro do CSS: as props de padding acabaram de ser limpas acima.
      const css = window.getComputedStyle(el);
      if (textoY) {
        const topo = parseFloat(css.paddingTop) || 0;
        const base = parseFloat(css.paddingBottom) || 0;
        el.style.setProperty('padding-top', `${Math.max(0, topo + textoY)}px`);
        el.style.setProperty('padding-bottom', `${Math.max(0, base - textoY)}px`);
        props.push('padding-top', 'padding-bottom');
      }
      if (textoX) {
        const esq = parseFloat(css.paddingLeft) || 0;
        const dir = parseFloat(css.paddingRight) || 0;
        el.style.setProperty('padding-left', `${Math.max(0, esq + textoX)}px`);
        el.style.setProperty('padding-right', `${Math.max(0, dir - textoX)}px`);
        props.push('padding-left', 'padding-right');
      }
    }
  }

  if (props.length) el.setAttribute(ATTR_PROPS, props.join('|'));
  if (vars.length) el.setAttribute(ATTR_VARS, vars.join('|'));
}

/**
 * Reaplica do zero os ajustes de UM cartão. Limpa o que o estúdio escreveu
 * antes (e só isso) e reescreve — chamada a cada render, é o que mantém tela e
 * arquivo idênticos depois de qualquer mudança de dado ou de layout.
 */
export function aplicarAjustes(raiz, ajustesDoCard, ativo = true) {
  if (!raiz) return;
  const marcados = [raiz, ...raiz.querySelectorAll(`[${ATTR_PROPS}], [${ATTR_VARS}]`)];
  marcados.forEach(limparElemento);
  if (!ativo || !AJUSTES_LIGADOS || !ajustesDoCard) return;
  Object.keys(ajustesDoCard).forEach((caminho) => {
    const el = resolverElemento(raiz, caminho);
    if (el) aplicarPatch(el, ajustesDoCard[caminho]);
  });
}

// Base da zona útil (config/storyCanvas.js). A limpeza do estúdio apaga a
// PROPRIEDADE inteira, e as vars da zona também são escritas por ele quando
// ajustadas — zerar um ajuste de zona deixaria `--story-safe-top` sem valor
// nenhum, e o SCSS usa a var sem fallback (`top: var(--story-safe-top)`): o
// bloco cairia para `auto`. Esta função recoloca a base onde ninguém escreveu.
const VARS_BASE = storySafeAreaVars();

export function garantirVarsBase(raiz) {
  if (!raiz?.style) return;
  Object.keys(VARS_BASE).forEach((nome) => {
    if (!raiz.style.getPropertyValue(nome)) raiz.style.setProperty(nome, VARS_BASE[nome]);
  });
}

/**
 * Aplica os ajustes no CLONE que vai virar PNG.
 *
 * É aqui que os ajustes valem de verdade: na tela, a amostra fica com o desenho
 * original (o estúdio desligado não mexe em nada do que o usuário vê) — o
 * html2canvas é que recebe a versão arrumada. Assim dá para acertar o arquivo
 * sem estragar a amostra, que é onde as duas coisas divergiam.
 *
 * O clone precisa já estar no documento: a composição do transform lê o
 * computed style para preservar a base do CSS.
 */
export function aplicarNaCaptura(clone) {
  if (!clone) return;
  const variante = clone.getAttribute?.('data-share-card') || '';
  const atual = estudioAtual();
  aplicarAjustes(clone, atual.ajustes[variante] || null, atual.aplicar);
  garantirVarsBase(clone);
}

/**
 * Tira do CLONE tudo que é moldura de edição. Chamado pelo shareCanvas antes da
 * foto: os ajustes ficam, a moldura do elemento selecionado some.
 */
export function limparEstudio(no) {
  if (!no) return;
  no.removeAttribute?.(ATTR_SEL);
  no.querySelectorAll?.(`[${ATTR_SEL}]`).forEach((el) => el.removeAttribute(ATTR_SEL));
  no.querySelectorAll?.('[data-studio-chrome]').forEach((el) => el.remove());
}

// ── estado compartilhado ─────────────────────────────────────────────────────
// Loja mínima com assinantes: o painel e cada ShareCard leem a MESMA verdade
// sem prop passando por três níveis (o ShareCard também é renderizado pelo
// prompt pós-registro, longe da tela do carrossel).
const inicial = () => {
  const base = { modo: false, aplicar: true, ajustes: clonar(AJUSTES_PADRAO), sel: null };
  try {
    const bruto = JSON.parse(localStorage.getItem(CHAVE) || 'null');
    if (!bruto || typeof bruto !== 'object') return base;
    return {
      modo: bruto.modo === true,
      aplicar: bruto.aplicar !== false,
      // O que está salvo no aparelho VENCE o arquivo: um deploy no meio da
      // edição não pode apagar o trabalho de quem está ajustando.
      ajustes: bruto.ajustes && typeof bruto.ajustes === 'object' ? bruto.ajustes : base.ajustes,
      sel: null,
    };
  } catch {
    return base;
  }
};

let estado = null;
const ouvintes = new Set();

const persistir = () => {
  try {
    localStorage.setItem(CHAVE, JSON.stringify({
      modo: estado.modo,
      aplicar: estado.aplicar,
      ajustes: estado.ajustes,
    }));
  } catch { /* storage indisponível — vale só nesta sessão */ }
};

export function estudioAtual() {
  if (!estado) estado = inicial();
  return estado;
}

function definir(mudanca) {
  estado = { ...estudioAtual(), ...mudanca };
  persistir();
  ouvintes.forEach((ouvir) => ouvir(estado));
}

// ── API ──────────────────────────────────────────────────────────────────────
export const estudioApi = {
  ligar: () => definir({ modo: true }),
  desligar: () => definir({ modo: false, sel: null }),
  alternarModo: () => definir({ modo: !estudioAtual().modo, sel: null }),
  aplicar: (ligado) => definir({ aplicar: ligado !== false }),
  selecionar: (variante, caminho) => definir({
    sel: variante && caminho ? { variante, caminho } : null,
  }),

  // Mescla `mudancas` no patch do elemento. Patch que volta a ser neutro é
  // removido — o JSON exportado não carrega dx:0.
  ajustar: (variante, caminho, mudancas) => {
    if (!variante || !caminho) return;
    const atual = estudioAtual();
    const doCard = { ...(atual.ajustes[variante] || {}) };
    const patch = { ...patchCompleto(doCard[caminho]), ...mudancas };
    if (patchVazio(patch)) delete doCard[caminho];
    else doCard[caminho] = patch;
    const ajustes = { ...atual.ajustes };
    if (Object.keys(doCard).length) ajustes[variante] = doCard;
    else delete ajustes[variante];
    definir({ ajustes });
  },

  limparElemento: (variante, caminho) => {
    const atual = estudioAtual();
    if (!atual.ajustes[variante]?.[caminho]) return;
    const doCard = { ...atual.ajustes[variante] };
    delete doCard[caminho];
    const ajustes = { ...atual.ajustes };
    if (Object.keys(doCard).length) ajustes[variante] = doCard;
    else delete ajustes[variante];
    definir({ ajustes });
  },

  limparCard: (variante) => {
    const atual = estudioAtual();
    if (!atual.ajustes[variante]) return;
    const ajustes = { ...atual.ajustes };
    delete ajustes[variante];
    definir({ ajustes });
  },

  limparTudo: () => definir({ ajustes: {}, sel: null }),

  // Volta para o que está assado em shareOverrides.js, jogando fora o que este
  // aparelho tinha guardado.
  restaurarPadrao: () => definir({ ajustes: clonar(AJUSTES_PADRAO), sel: null }),

  exportarJson: () => {
    const limpo = {};
    const { ajustes } = estudioAtual();
    Object.keys(ajustes).sort().forEach((variante) => {
      const doCard = {};
      Object.keys(ajustes[variante] || {}).sort().forEach((caminho) => {
        const patch = ajustes[variante][caminho];
        if (patchVazio(patch)) return;
        // Só o que diverge do neutro vai para o arquivo.
        const cheio = patchCompleto(patch);
        const enxuto = {};
        Object.keys(cheio).forEach((chave) => {
          const valor = cheio[chave];
          if (valor !== PATCH_VAZIO[chave] && valor !== null && valor !== '') enxuto[chave] = valor;
        });
        if (Object.keys(enxuto).length) doCard[caminho] = enxuto;
      });
      if (Object.keys(doCard).length) limpo[variante] = doCard;
    });
    return JSON.stringify(limpo, null, 2);
  },

  importarJson: (texto) => {
    const lido = JSON.parse(String(texto || '').trim() || '{}');
    if (!lido || typeof lido !== 'object' || Array.isArray(lido)) {
      throw new Error('O JSON precisa ser um objeto de artes.');
    }
    definir({ ajustes: lido, sel: null });
  },
};

// ── hooks ────────────────────────────────────────────────────────────────────
// Assinatura completa: usada pelo painel, que precisa de tudo.
export function useEstudio() {
  const [snap, setSnap] = useState(estudioAtual);
  useEffect(() => {
    const ouvir = (novo) => setSnap(novo);
    ouvintes.add(ouvir);
    ouvir(estudioAtual());
    return () => { ouvintes.delete(ouvir); };
  }, []);
  return snap;
}

/**
 * Só o interruptor do editor. A TELA de compartilhar usa este, não o
 * `useEstudio`: assinar a loja inteira faria a tela re-renderizar (e com ela os
 * 9 cartões do carrossel) a cada quadro do arrasto de um único elemento.
 */
export function useModoEstudio() {
  const [modo, setModo] = useState(() => estudioAtual().modo);
  useEffect(() => {
    const ouvir = () => setModo(estudioAtual().modo);
    ouvintes.add(ouvir);
    ouvir();
    return () => { ouvintes.delete(ouvir); };
  }, []);
  return modo;
}

/**
 * Fatia de UMA arte. Existe para o arrasto não custar caro: mexer no cartão 4
 * não pode re-renderizar os outros 8 do carrossel a cada quadro do dedo. Só
 * re-renderiza quando a fatia daquela variante muda de verdade.
 */
export function useEstudioDoCard(variante) {
  const fatia = () => {
    const atual = estudioAtual();
    return {
      aplicar: atual.aplicar,
      modo: atual.modo,
      ajustes: atual.ajustes[variante] || null,
      selecionado: atual.sel?.variante === variante ? atual.sel.caminho : null,
    };
  };
  const [snap, setSnap] = useState(fatia);
  useEffect(() => {
    const ouvir = () => setSnap((anterior) => {
      const novo = fatia();
      const igual = anterior.aplicar === novo.aplicar
        && anterior.modo === novo.modo
        && anterior.ajustes === novo.ajustes
        && anterior.selecionado === novo.selecionado;
      return igual ? anterior : novo;
    });
    ouvintes.add(ouvir);
    ouvir();
    return () => { ouvintes.delete(ouvir); };
    // eslint-disable-next-line
  }, [variante]);
  return snap;
}

// Atalho de console para destravar/travar sem depender do toque longo — útil no
// Chrome remoto ligado no aparelho, onde o dedo já está ocupado segurando ele.
if (typeof window !== 'undefined') {
  window.lesvitaShareStudio = {
    on: () => estudioApi.ligar(),
    off: () => estudioApi.desligar(),
    json: () => estudioApi.exportarJson(),
    load: (texto) => estudioApi.importarJson(texto),
    reset: () => estudioApi.limparTudo(),
  };
}
